/**
 * QuranWordRenderer
 *
 *  Renders a verse's Arabic text word-by-word. Each word is clickable to open
 *  the lexical analysis panel. When no lexical data is available for the
 *  ayah (e.g., morphology not yet imported), it gracefully degrades to a
 *  static rendering identical to the legacy <ArabicVerse>.
 *
 *  Tajwīd-aware: when `tajwidAnnotations` is provided (an array of
 *  {start, end, rule, color, label, harakat} computed on the FULL ayah
 *  text), we colorise the **inner** content of each clickable word using
 *  TajwidColoredText. The click target stays at the word level so the
 *  lexical drawer can always be opened, even when Tajwīd is enabled.
 */
import React, { useMemo } from 'react';
import { useSurahLexicalWords } from '../../lexical/useSurahLexicalWords';
import TajwidColoredText from '../quran/TajwidColoredText';
import TajwidExercisePopover from '../quran/TajwidExercisePopover';

/**
 * Split the text into words while keeping their character offsets so we can
 * correctly intersect with global Tajwīd annotations.
 * @returns {Array<{text:string, start:number, end:number}>}
 */
const splitVerseWordsWithOffsets = (text) => {
  if (!text) return [];
  const out = [];
  const re = /\S+/g;
  let m;
  while ((m = re.exec(text)) !== null) {
    out.push({ text: m[0], start: m.index, end: m.index + m[0].length });
  }
  return out;
};

/**
 * For a given word at [wordStart, wordEnd) in the source text, return the
 * subset of `annotations` (each {start, end, …} on the FULL text) that
 * overlap the word, with offsets translated to be RELATIVE to the word.
 */
const sliceAnnotationsForWord = (annotations, wordStart, wordEnd) => {
  if (!annotations || !annotations.length) return [];
  const out = [];
  for (const a of annotations) {
    if (a.end <= wordStart || a.start >= wordEnd) continue;
    const start = Math.max(a.start, wordStart) - wordStart;
    const end = Math.min(a.end, wordEnd) - wordStart;
    if (end > start) {
      out.push({ ...a, start, end });
    }
  }
  return out;
};

export const QuranWordRenderer = ({
  surahNumber,
  ayahKey,
  text,
  onWordClick,
  enabled = true,
  className = '',
  highlightWordIndex = null,
  tajwidAnnotations = null,
  exercise = null,                 // useTajwidExercise() return value or null
}) => {
  const { byWord } = useSurahLexicalWords(surahNumber, ayahKey);

  const visualWords = useMemo(() => splitVerseWordsWithOffsets(text), [text]);
  const exerciseActive = !!(exercise && exercise.enabled);

  if (!enabled && !exerciseActive) {
    // Fully disabled: static rendering. Still apply Tajwīd colors if provided.
    return (
      <p
        className={`quran-arabic-text text-text-primary ${className}`}
        dir="rtl"
        data-testid={`verse-arabic-${ayahKey}`}
      >
        {tajwidAnnotations && tajwidAnnotations.length > 0 ? (
          <TajwidColoredText text={text} annotations={tajwidAnnotations} />
        ) : (
          text
        )}
      </p>
    );
  }

  const tajwidEnabled = Array.isArray(tajwidAnnotations) && tajwidAnnotations.length > 0;

  return (
    <p
      className={`quran-arabic-text text-text-primary ${className}`}
      dir="rtl"
      data-testid={`verse-arabic-${ayahKey}`}
      {...(tajwidEnabled ? { 'data-tajwid-enabled': 'true' } : {})}
    >
      {visualWords.map((w, i) => {
        const wordIndex = i + 1;
        const segments = byWord.get(wordIndex) || null;
        const isHighlighted = highlightWordIndex === wordIndex;
        const wordAnns = tajwidEnabled
          ? sliceAnnotationsForWord(tajwidAnnotations, w.start, w.end)
          : [];
        const handle = (e) => {
          e.preventDefault();
          e.stopPropagation();
          onWordClick && onWordClick({
            ayahKey,
            wordIndex,
            displayText: w.text,
            segments,
          });
        };

        // -- Exercise mode: target words become QCM popovers; others
        // are rendered statically (not clickable for lexical drawer).
        if (exerciseActive) {
          const target = exercise.targetFor(ayahKey, wordIndex);
          if (target) {
            const ans = exercise.answerFor(ayahKey, wordIndex);
            const inner = wordAnns.length > 0 && (ans || false)
              ? <TajwidColoredText text={w.text} annotations={wordAnns} />
              : w.text;
            return (
              <React.Fragment key={`${ayahKey}-${wordIndex}`}>
                <TajwidExercisePopover
                  target={target}
                  answer={ans}
                  choices={exercise.choicesFor(target)}
                  onChoose={(rule) => exercise.answer(target, rule)}
                  ariaLabel={`Mot ${wordIndex} : ${w.text} — règle Tajwīd à identifier`}
                  testIdSuffix={`${ayahKey}-${wordIndex}`}
                >
                  {inner}
                </TajwidExercisePopover>
                {i < visualWords.length - 1 ? ' ' : ''}
              </React.Fragment>
            );
          }
          // Non-target word in exercise mode: dimmed, non-interactive
          return (
            <React.Fragment key={`${ayahKey}-${wordIndex}`}>
              <span
                data-word-index={wordIndex}
                data-testid={`quran-word-${ayahKey}-${wordIndex}`}
                className="inline-block opacity-60"
              >
                {w.text}
              </span>
              {i < visualWords.length - 1 ? ' ' : ''}
            </React.Fragment>
          );
        }

        return (
          <React.Fragment key={`${ayahKey}-${wordIndex}`}>
            <span
              role="button"
              tabIndex={0}
              data-word-index={wordIndex}
              data-testid={`quran-word-${ayahKey}-${wordIndex}`}
              onClick={handle}
              onMouseDown={(e) => e.stopPropagation()}
              onTouchStart={(e) => e.stopPropagation()}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') handle(e);
              }}
              className={[
                'inline-block cursor-pointer transition-colors duration-150 rounded px-0.5',
                'hover:bg-primary/10 hover:text-primary',
                'focus:outline-none focus:ring-2 focus:ring-primary/50',
                isHighlighted ? 'bg-primary/20 text-primary' : '',
              ].join(' ')}
              aria-label={`Mot ${wordIndex} : ${w.text}`}
            >
              {wordAnns.length > 0 ? (
                <TajwidColoredText text={w.text} annotations={wordAnns} />
              ) : (
                w.text
              )}
            </span>
            {/* Restore inter-word whitespace */}
            {i < visualWords.length - 1 ? ' ' : ''}
          </React.Fragment>
        );
      })}
    </p>
  );
};
